import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import EyeIcon from "../../assets/image/eye-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/trip-assignment-management";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const mapRouteRow = (r) => {
  const statusNum = r.status === 1 || r.status === "1" ? 1 : 0;
  return {
    id: r._id,
    routeId:
      r.route_ref_id != null && r.route_ref_id !== ""
        ? `${r.route_ref_id}`
        : "—",
    companyName: r.company_id?.name ?? "—",
    companyLocation: r.company_id?.company_location_id?.name ?? "—",
    miplLocation: r.mipl_location_id?.name ?? "—",
    status: statusNum,
    statusLabel: statusNum === 1 ? "Active" : "Inactive",
    raw: r,
  };
};

const TripAssignmentManagement = () => {
  const [searchParams] = useSearchParams();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));

  const applyRestoredState = useCallback((saved) => {
    if (saved.search != null) setSearch(saved.search);
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    if (saved.limit) {
      const l = Number(saved.limit);
      if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
    }
  }, []);

  const { detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      limit: itemsPerPage,
      search: search.trim() || undefined,
    },
    applyRestoredState
  );

  useEffect(() => {
    let cancelled = false;

    const loadRoutes = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setRoutes([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfRoute}?page=${currentPage}&limit=${itemsPerPage}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setRoutes([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setRoutes([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.route_data) ? data.route_data : [];

        setRoutes(list.map(mapRouteRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load routes."
          );
          setRoutes([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadRoutes();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage]);

  const filteredRoutes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter(
      (row) =>
        String(row.routeId).toLowerCase().includes(q) ||
        String(row.companyName).toLowerCase().includes(q) ||
        String(row.companyLocation).toLowerCase().includes(q) ||
        String(row.miplLocation).toLowerCase().includes(q)
    );
  }, [routes, search]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const totalPages = apiTotalPages;
  const serialBase = search.trim() ? 0 : indexOfFirst;

  return (
    <>
      <div className="tab-pane-content mt-7">
        <div className="flex gap-x-2.5 mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearch(normalizeSearchInput(e.target.value))}
          />
        </div>

        <div className="common-table-wrapper m-0">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Route ID</th>
                  <th>Company Name</th>
                  <th>Company Location</th>
                  <th>MIPL Location</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      Loading routes…
                    </td>
                  </tr>
                ) : filteredRoutes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      No routes found.
                    </td>
                  </tr>
                ) : (
                  filteredRoutes.map((row, index) => (
                    <tr key={row.id}>
                      <td>{serialBase + index + 1}</td>
                      <td>{row.routeId}</td>
                      <td>{row.companyName}</td>
                      <td>{row.companyLocation}</td>
                      <td>{row.miplLocation}</td>
                      <td>{row.statusLabel}</td>

                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/trip-assignment-management/view-trip?route_id=${encodeURIComponent(
                              row.id
                            )}${detailListQuery}`}
                          >
                            <img src={EyeIcon} className="h-7 min-w-[22px]" alt="View" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-x-4 items-center mt-4">
          <select
            className="p-2 shadow-sm outline-none"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>

          <div className="text-sm text-gray-500 ml-auto">
            {totalRecords} records found
          </div>

          <div className="flex items-center gap-2 pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === 1 ? "" : "cursor-pointer"
              }`}
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === totalPages ? "" : "cursor-pointer"
              }`}
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>

      <Toastify />
    </>
  );
};

export default TripAssignmentManagement;
